/**
 * Five kinds of work (owner, 2026-09-28): CLEAN · MAINTAIN · LOG · REPORT · STOCK. Everything someone does in the app
 * is one of them; a routine (opening, closing, weekly) is a MIX of them at a moment of the day, never a sixth kind.
 * Every item carries the same two things: How (the SOP / step by step) and Buy (the products it needs).
 *
 * Gates (docs/ACTIVITIES.md):
 * | K1 Exhaustive | Every template, task, chore, incident and supply maps to exactly one kind (a test enumerates them) |
 * | K2 One shape | An ActivityItem always has kind, title, how (optional only when there's nothing to learn) and needs |
 * | K3 Needs are sourced | Products come from the item's own sourced guide (howto.needSupplies, surface products, sanitizer
 *   table); none invented |
 */
import type { TaskCategory } from "./model.ts";
import type { ChoreArea } from "./home/chores.ts";

export type ActivityKind = "clean" | "maintain" | "log" | "report" | "stock";

export const ACTIVITY_KINDS: Record<ActivityKind, { label: string; verb: string; icon: string; means: string }> = {
  clean: { label: "Clean", verb: "Clean", icon: "spray", means: "Wash, rinse, sanitize or disinfect a surface, tool or room." },
  maintain: { label: "Maintain", verb: "Maintain", icon: "wrench", means: "Keep equipment working: descale, change filters, calibrate, service." },
  log: { label: "Log", verb: "Log", icon: "write", means: "Write down a reading or a check, so you can prove it was done." },
  report: { label: "Report", verb: "Report", icon: "sign", means: "Tell someone about a problem: a pest, a spill, a sick person, broken equipment." },
  stock: { label: "Stock", verb: "Stock", icon: "box", means: "Keep supplies and food on hand, rotated and in date." },
};

/** Sheet templates: most are logs; a few are really stock work (receiving) or cleaning sign-offs. */
const TEMPLATE_KIND: Record<string, ActivityKind> = {
  receiving: "stock", cleaning_signoff: "clean", pest_sightings: "report", calibration: "maintain",
};
export function kindOfTemplate(templateId: string): ActivityKind {
  return TEMPLATE_KIND[templateId] ?? "log";
}

const TASK_KIND: Record<TaskCategory, ActivityKind> = {
  opening: "log", closing: "clean", cleaning: "clean", event: "log", pest: "report", maintenance: "maintain", training: "log",
};
/** Tasks: by category, but a task whose words are clearly another kind wins ("Restock the restroom" is stock). */
export function kindOfTask(t: { category: TaskCategory; title: string }): ActivityKind {
  const w = t.title.toLowerCase();
  if (/\b(restock|refill|reorder|order|rotate|fifo|put away|receive)\b/.test(w)) return "stock";
  if (/\b(descale|filter|calibrat|service|replace|inspect|test the|backflow|delime)\b/.test(w)) return "maintain";
  return TASK_KIND[t.category];
}

const CHORE_KIND: Record<ChoreArea, ActivityKind> = {
  food: "clean", bathroom: "clean", floors: "clean", bedroom: "clean", dusting: "clean", laundry: "clean", appliances: "maintain", storage: "stock",
};
export function kindOfChore(c: { area: ChoreArea; title: string; id?: string }): ActivityKind {
  const w = `${c.id ?? ""} ${c.title}`.toLowerCase();
  if (/\buse-?by\b|\bpantry\b|\bexpir|\bfifo\b/.test(w)) return "stock";
  if (/\bfridge (temp|temperature)\b|\bthermometer\b/.test(w)) return "log";
  if (/\bdescal|\bfilter\b|\bmattress\b|\bcoil|\bwasher clean|\bclean cycle\b/.test(w)) return "maintain";
  return CHORE_KIND[c.area];
}

/** Incidents are reports; supplies are stock. */
export const kindOfIncident = (): ActivityKind => "report";
export const kindOfSupply = (): ActivityKind => "stock";

/** One product an item needs, and how to get it (the apps render Kept · Reorder/Call · Find it on Amazon). */
export interface ActivityNeed { supplyKey?: string; product: string }

/** The one shape every routine item takes (K2). */
export interface ActivityItem {
  kind: ActivityKind;
  id: string;
  title: string;
  /** When it's due, if scheduled. */
  dueAt?: string;
  /** Due before the place's first full operating day (schedule.ts ExpectedCheck.firstDay): never shown as "Was due". */
  firstDay?: true;
  /**
   * Where "How" goes: a check template's how-to, a surface/SOP, an Easy procedure. A checklist task also carries its
   * starter key (tasks.ts, for "How we do it here": customSteps.ts ourWayKeys), its own one-line how (`text`, shown
   * when no sourced guide covers it), more surface guides it spans, and the library topic with the rules behind it.
   */
  how?: { templateId?: string; surfaceId?: string; sopId?: string; easyId?: string; taskKey?: string; text?: string; alsoSurfaceIds?: string[]; libraryId?: string };
  needs: ActivityNeed[];
  /** What the row is about, so Done can act on it without parsing the id. */
  ref?: { type: "check" | "task" | "object" | "incident" | "supply"; id: string };
}

/** A routine = a mix of kinds at a moment; ordered safety-first: report, log, clean, maintain, stock. */
const ORDER: Record<ActivityKind, number> = { report: 0, log: 1, clean: 2, maintain: 3, stock: 4 };
export function routine(items: ActivityItem[]): ActivityItem[] {
  return [...items].sort((a, b) => ORDER[a.kind] - ORDER[b.kind] || (a.dueAt ?? "").localeCompare(b.dueAt ?? "") || a.title.localeCompare(b.title));
}
export function byKind(items: ActivityItem[]): Record<ActivityKind, ActivityItem[]> {
  const out = { clean: [], maintain: [], log: [], report: [], stock: [] } as Record<ActivityKind, ActivityItem[]>;
  for (const it of items) out[it.kind].push(it);
  return out;
}
